import dayjs,{ type Dayjs } from "dayjs";
import { CONTRACT_STATUS_ACTIVE,CONTRACT_STATUS_CANCELLED,CONTRACT_STATUS_DRAFT,CONTRACT_STATUS_ENDED,CONTRACT_STATUS_PAUSED } from "../../../app/constants/contractStatus";
import {
CONTRIBUTION_STATUS_CANCELLED,
CONTRIBUTION_STATUS_DUE,
CONTRIBUTION_STATUS_OVERDUE,
CONTRIBUTION_STATUS_PENDING
} from "../../../app/constants/contributionCalendarStatus";
import {
CYCLE_UNIT_DAILY,
CYCLE_UNIT_MONTHLY,
CYCLE_UNIT_WEEKLY,
CYCLE_UNIT_YEARLY
} from "../../../app/constants/cycleUnit";
import { TIME_UNIT_DAYS,TIME_UNIT_MONTHS,TIME_UNIT_WEEKS,TIME_UNIT_YEARS } from "../../../app/constants/timeUnit";
import type {ApiContractPackageDto,ContractRow} from "./personDetailTypes";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";
import { PERSON_DETAIL_TAB_KEY } from "./personDetailValues";

const SEPA_DIRECT_DEBIT_RUNS_PATH = "/finance/sepa-direct-debit-runs";

/** Synthetic Select value: Preisanpassungs-Drawer fuer Bausteine ohne Vertragspaket. */
export const CONTRACT_PRICE_ADJUSTMENT_NO_PACKAGE_OPTION = "__no_package__";

export const PERSON_DETAIL_ALLOWED_TABS = new Set([
  "overview",
  "profile",
  "contracts",
  "restPeriods",
  "priceAdjustments",
  "statement",
  "contributions",
  "openitems",
  "wallets",
  "promotions",
  "payments",
  "articleSales",
  "files",
  "changes"
]);

export function normalizePersonDetailTabParam(value: string | null | undefined): string {
  const raw = String(value || "").trim();
  if (raw === PERSON_DETAIL_TAB_KEY.legacyDocuments || raw === PERSON_DETAIL_TAB_KEY.legacyDocument) return "files";
  return PERSON_DETAIL_ALLOWED_TABS.has(raw) ? raw : "overview";
}

/** Nur nach expliziter Beitrags-Stornierung (API); alte Lifecycle-Cancelled ohne IsManual = weiterhin fällig. */
export function isContributionWaived(r: { status?: number; isManual?: boolean; cancelledAmount?: number }): boolean {
  return Number(r.status || 0) === CONTRIBUTION_STATUS_CANCELLED && r.isManual === true && !r.cancelledAmount;
}

export function contributionCycleUnitToDayjsPeriodUnit(
  cycleUnitRaw: number | null | undefined
): "day" | "week" | "month" | "year" {
  const u = Number(cycleUnitRaw || 0);
  if (u === CYCLE_UNIT_MONTHLY) return "month";
  if (u === CYCLE_UNIT_WEEKLY) return "week";
  if (u === CYCLE_UNIT_DAILY) return "day";
  if (u === CYCLE_UNIT_YEARLY) return "year";
  return "day";
}

/** Entspricht CalculateContributionStatus im Backend (Mahnstufe-Toleranz 0 = wie Fälligkeitstag). */
export function contributionStatusFromDueDateForDisplay(dueDateIso: string, daysAfterDueForOverdue = 0): number {
  const raw = String(dueDateIso || "").slice(0, ISO_DATE_LENGTH);
  const due = dayjs(raw);
  if (!due.isValid()) return CONTRIBUTION_STATUS_PENDING;
  const today = dayjs().startOf("day");
  const overdueBoundary = due.add(daysAfterDueForOverdue, "day");
  if (today.isAfter(overdueBoundary, "day") || today.isSame(overdueBoundary, "day")) {
    return CONTRIBUTION_STATUS_OVERDUE;
  }
  if (due.isBefore(today, "day") || due.isSame(today, "day")) {
    return CONTRIBUTION_STATUS_DUE;
  }
  return CONTRIBUTION_STATUS_PENDING;
}

export function getByPath(obj: unknown, path: string): unknown {
  if (!obj) return undefined;
  const parts = String(path).split(".");
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

/** Pfad -> i18n-LabelKey fuer "zurueck zu ..." auf der Personenseite. */
const BACK_LABEL_BY_PATH: Record<string, string> = {
  "/persons": "Back to People",
  "/persons/members": "Back to Members",
  "/persons/prospects": "Back to Prospects",
  "/persons/former": "Back to Former",
  "/finance/open-items": "Back to Open items",
  "/finance/reminders": "Back to Reminders",
  "/finance/sepa-direct-debit-runs": "Back to direct debits",
  "/finance": "Back to Finances",
  "/checkin": "Back to Check-in",
  "/leads": "Back to Leads",
  "/sales/signing": "Back to sales",
  "/tools/duplicate-finder": "Back to duplicate finder",
  "/overview": "Navigation.backToOverview",
  "/communication/inbox": "Back to inbox",
  "/tasks": "Back to tasks",
  "/reports/members/birthdays": "Back to birthday list"
};

const PERSON_BACK_TARGET_STORAGE_KEY = "personDetail.backTarget";

/**
 * Herkunft der Personenakte über einen Reload hinweg merken.
 *
 * `location.state.from` überlebt weder ein Neuladen noch eine Folgenavigation innerhalb der Akte
 * (Tabwechsel über `setSearchParams` verwirft den State). Ohne diesen Speicher fiel die Akte auf
 * "/persons" zurück — deshalb landete man aus Interessenten oder Ehemaligen immer wieder bei den
 * Mitgliedern. sessionStorage statt localStorage: die Herkunft gilt für diesen Tab, nicht dauerhaft.
 */
export function rememberPersonBackTarget(personId: string, from: string): void {
  if (!personId || !from) return;
  try {
    sessionStorage.setItem(PERSON_BACK_TARGET_STORAGE_KEY, JSON.stringify({ personId, from }));
  } catch {
    // Privater Modus / voller Speicher: die Herkunft ist dann nur nicht persistent.
  }
}

export function readPersonBackTarget(personId: string): string | null {
  if (!personId) return null;
  try {
    const raw = sessionStorage.getItem(PERSON_BACK_TARGET_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { personId?: unknown; from?: unknown };
    if (String(parsed?.personId ?? "") !== personId) return null;
    const from = String(parsed?.from ?? "").trim();
    return from || null;
  } catch {
    return null;
  }
}

export function backLabelKeyForPath(path: string): string | null {
  const base = (path || "").split("?")[0].trim();
  if (!base) return null;
  if (base.startsWith(`${SEPA_DIRECT_DEBIT_RUNS_PATH}/`) && base !== SEPA_DIRECT_DEBIT_RUNS_PATH) {
    return "Back to direct debit run";
  }
  if (BACK_LABEL_BY_PATH[base]) return BACK_LABEL_BY_PATH[base];
  const sorted = Object.keys(BACK_LABEL_BY_PATH).sort((a, b) => b.length - a.length);
  const match = sorted.find((p) => base === p || base.startsWith(p + "/") || base.startsWith(p + "?"));
  return match ? BACK_LABEL_BY_PATH[match] : null;
}

/** API-Antwort `/api/v1/contract-packages` -> Tabellenzeile Paket. */
export function mapApiPackageToContractRow(x: ApiContractPackageDto): ContractRow {
  return {
    id: String(x.id),
    status: Number(x.status || 0),
    planName: String(x.planName || ""),
    startDate: String(x.startDate || ""),
    endDate: x.effectiveMembershipEndDate ?? x.primaryContractEndDate ?? null,
    closingDate: x.closingDate != null ? String(x.closingDate).slice(0, ISO_DATE_LENGTH) : null,
    nextPossibleMembershipEndDate: x.nextPossibleMembershipEndDate != null ? String(x.nextPossibleMembershipEndDate).slice(0, ISO_DATE_LENGTH) : null,
    priceOverride: Number(x.priceOverride || 0),
    closingAccountName: x.closingAccountName ?? null,
    note: x.note ?? null,
    createdAt: String(x.createdAt || ""),
    studioId: x.studioId ?? null,
    billingCycle: x.billingCycle ?? null,
    termDefinitionId: x.termDefinitionId ?? null,
    termName: x.termName ?? null,
    termUnit: x.termUnit ?? null,
    termValue: x.termValue ?? null,
    restPeriodTemplateId: x.restPeriodTemplateId ? String(x.restPeriodTemplateId) : null,
    restPeriodTemplateName: x.restPeriodTemplateName ?? null,
    cancellationModelId: x.cancellationModelId ? String(x.cancellationModelId) : null,
    cancellationModelName: x.cancellationModelName ?? null,
    accessScope: x.accessScope ?? null,
    allowedStudioIds: (x.allowedStudioIds || x.allowedStudioIdsSnapshot || []).map(String),
    requiresNoArrears: x.requiresNoArrears !== false,
    contractTemplateVariantId: x.contractTemplateVariantId ?? null,
    paymentPlanId: x.paymentPlanId ? String(x.paymentPlanId) : null,
    paymentPlanInstallmentCount: x.paymentPlanInstallmentCount ?? null,
    paymentPlanInstallmentAmount: x.paymentPlanInstallmentAmount != null ? Number(x.paymentPlanInstallmentAmount) : null,
    paymentMethod: x.paymentMethod != null ? Number(x.paymentMethod) : null,
    bundleNames: (x.bundleNames || []).map(String),
    salesPromotionNames: (x.salesPromotionNames || []).map(String),
    cancellationRequestedDate: x.cancellationRequestedDate != null ? String(x.cancellationRequestedDate).slice(0, ISO_DATE_LENGTH) : null,
    cancellationEffectiveDate: x.cancellationEffectiveDate != null ? String(x.cancellationEffectiveDate).slice(0, ISO_DATE_LENGTH) : null,
    cancellationReasonId: x.cancellationReasonId != null ? String(x.cancellationReasonId) : null,
    cancellationNote: x.cancellationNote ?? null
  };
}

export function parseDateOnly(value?: string | null): Dayjs | null {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
}

function normalizeDateOnlyForSort(value?: string | null): string {
  const raw = String(value || "").slice(0, ISO_DATE_LENGTH);
  if (!raw) return "";
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}

export function compareDateOnlyDesc(leftRaw?: string | null, rightRaw?: string | null): number {
  const left = normalizeDateOnlyForSort(leftRaw);
  const right = normalizeDateOnlyForSort(rightRaw);
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  return right.localeCompare(left);
}

export function compareTextDesc(leftRaw?: string | null, rightRaw?: string | null): number {
  const left = String(leftRaw || "").trim();
  const right = String(rightRaw || "").trim();
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  return right.localeCompare(left);
}

export { formatDateOnlyIso } from "../../../app/utils/formatDateOnlyIso";

export { applyPaymentPlanRounding, computeInstallmentsPreviewWithRounding, round2 } from "../../../app/utils/paymentPlanInstallments";

export function contractStatusLabel(status: unknown, text: (key: string) => string): string {
  const s = Number(status || 0);
  if (s === CONTRACT_STATUS_DRAFT) return text("Draft");
  if (s === CONTRACT_STATUS_ACTIVE) return text("Active");
  if (s === CONTRACT_STATUS_PAUSED) return text("Paused");
  if (s === CONTRACT_STATUS_CANCELLED) return text("Cancelled");
  if (s === CONTRACT_STATUS_ENDED) return text("Ended");
  return String(status ?? "");
}

export function timeUnitLabel(u: number | null | undefined, text: (key: string) => string): string {
  const n = u == null ? null : Number(u);
  if (n === TIME_UNIT_DAYS) return text("Days");
  if (n === TIME_UNIT_WEEKS) return text("Weeks");
  if (n === TIME_UNIT_MONTHS) return text("Months");
  if (n === TIME_UNIT_YEARS) return text("Years");
  return "";
}

/**
 * Abgelaufen nach Datum: Enddatum erreicht (oder Beginn = Ende), auch wenn der gespeicherte Status noch „aktiv“ ist.
 * Eine Regel für Anzeige „beendet“ und Kündbarkeit in der Vertragsliste.
 */
export function isEndedByDate(row: { startDate?: string | null; endDate?: string | null }, today = dayjs().format("YYYY-MM-DD")): boolean {
  const endDate = row.endDate ? String(row.endDate).slice(0, ISO_DATE_LENGTH) : "";
  const startDate = row.startDate ? String(row.startDate).slice(0, ISO_DATE_LENGTH) : "";
  return Boolean(endDate && (endDate <= today || endDate === startDate));
}
